/**
 * KioskShell — full-screen, large-tap-target shell for the 43" kiosk.
 *
 * Side-nav on the left with reserved tabs (Home / History / Documents /
 * Profile). Score-tile region fills the remaining viewport. The
 * "Hand to next patient" reset button replaces the regular logout
 * button in this form factor.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ActivitySquare,
  FileText,
  FlaskConical,
  Home as HomeIcon,
  LineChart,
  Sparkles,
  User,
} from 'lucide-react';
import { logout } from '@cardioq/api-client';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/useAuth';
import { PATIENT_SIGN_IN } from '@/lib/signInPath';
import { IdleWatchdog } from './IdleWatchdog';
import { Logo } from './Logo';

// Dev-only items (the engine sandbox) never ship in a built release.
const NAV = [
  { to: '/home', label: 'Home', icon: HomeIcon },
  { to: '/history', label: 'History', icon: LineChart },
  { to: '/documents', label: 'Documents', icon: FileText },
  { to: '/methodology', label: 'How it works', icon: Sparkles },
  // DEV-only — remove with the /compute route before production
  { to: '/compute', label: 'Compute Test', icon: FlaskConical, dev: true },
  { to: '/profile', label: 'Profile', icon: User },
].filter((item) => !item.dev || import.meta.env.DEV);

export function KioskShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { logout: clearLocalAuth } = useAuth();
  const navigate = useNavigate();

  async function handleReset() {
    await logout();
    clearLocalAuth();
    navigate(PATIENT_SIGN_IN, { replace: true });
  }

  return (
    <div className="flex min-h-screen flex-row bg-brand-bg text-brand-slate-100">
      {/* Side nav */}
      <aside className="flex w-72 flex-col border-r border-white/[0.06] bg-brand-surface/40 backdrop-blur-md">
        <div className="px-6 py-7">
          <Logo className="h-14 w-14" />
        </div>

        <nav className="flex-1 px-3 pt-2">
          {NAV.map(({ to, label, icon: Icon, dev }) => {
            const active = pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  'flex items-center gap-3 rounded-xl px-4 py-4 text-base font-semibold transition-colors focus-ring',
                  'min-h-[60px]', // kiosk tap target
                  active
                    ? 'bg-brand-slate-800 text-brand-slate-50'
                    : 'text-brand-slate-300 hover:bg-white/[0.04]',
                )}
              >
                <Icon
                  className={cn(
                    'h-5 w-5',
                    active ? 'text-brand-cyan-400' : 'text-brand-slate-400',
                  )}
                />
                <span className="flex-1">{label}</span>
                {dev ? (
                  <span className="rounded-full bg-brand-pulse-400/[0.15] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-brand-pulse-200">
                    Dev
                  </span>
                ) : null}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-white/[0.06] p-4">
          <button
            type="button"
            onClick={handleReset}
            className="flex w-full min-h-[60px] items-center justify-center gap-2 rounded-xl border border-brand-pulse-400/30 bg-brand-pulse-400/[0.06] px-4 py-3 text-base font-semibold text-brand-pulse-200 transition-colors hover:bg-brand-pulse-400/[0.12] focus-ring"
          >
            <ActivitySquare className="h-5 w-5" />
            Hand to next patient
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-10 py-12">{children}</div>
      </main>

      <IdleWatchdog />
    </div>
  );
}
